import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ScrollArea } from '@/components/ui/scroll-area';
import { motion, useReducedMotion } from 'framer-motion';
import {
  PenLine,
  Sparkles,
  Type,
  AlignLeft,
  MousePointerClick,
  Layers,
  Camera,
  Check,
  RefreshCw,
  Plus,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import OrbisSpinner from '@/components/ui/orbis-spinner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

function safeText(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    return value.text || value.label || value.name || value.title || value.content || value.value || value.description || value.alt || value.src || JSON.stringify(value);
  }
  return String(value);
}

const ICON_MAP = {
  stat: Layers,
  feature: Sparkles,
  testimonial: AlignLeft,
  step: Layers,
  pricing_tier: Layers,
};

const ASSET_TYPE_LABELS = {
  photo: { label: 'Photo', color: 'var(--orbis-blue)' },
  ui_asset: { label: 'UI Asset', color: 'var(--orbis-amber)' },
};

const SECTION_LABELS = {
  headline: 'Headline',
  subheadline: 'Subheadline',
  body_copy: 'Body',
  ctas: 'CTA',
  content_items: 'Supporting Content',
  art_direction: 'Art Direction',
};

function ToneLabel({ children, tone = 'default' }) {
  const color = tone === 'accent' ? 'var(--orbis-blue)' : 'var(--orbis-t4)';
  return (
    <span
      style={{
        fontSize: 10,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color,
        fontFamily: 'var(--orbis-mono)',
      }}
    >
      {children}
    </span>
  );
}

function InlineTextEdit({ label, value, onChange, multiline = false, placeholder = '' }) {
  const Tag = multiline ? 'textarea' : 'input';
  return (
    <div className="space-y-2">
      <ToneLabel>{label}</ToneLabel>
      <Tag
        autoFocus
        value={value || ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        rows={multiline ? 4 : undefined}
        className="w-full rounded-xl px-4 py-3 outline-none resize-none"
        style={{
          background: 'var(--surface-1)',
          border: '1px solid var(--border-active)',
          color: 'var(--orbis-t1)',
          fontSize: multiline ? 14 : 15,
          lineHeight: 1.6,
        }}
      />
    </div>
  );
}

function CTAEditor({ ctas, onChange }) {
  const items = ctas?.length ? ctas : [];

  const updateCta = (index, field, value) => {
    const next = [...items];
    next[index] = { ...next[index], [field]: value };
    onChange(next);
  };

  const addCta = () => {
    onChange([...items, { label: '', style: items.length === 0 ? 'primary' : 'secondary' }]);
  };

  const removeCta = (index) => {
    onChange(items.filter((_, idx) => idx !== index));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <ToneLabel>Call to Action</ToneLabel>
        <button
          onClick={addCta}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px]"
          style={{ border: '1px solid var(--orbis-s3)', color: 'var(--orbis-t2)' }}
        >
          <Plus className="w-3 h-3" />
          Add CTA
        </button>
      </div>
      {items.length === 0 ? (
        <div className="rounded-xl px-4 py-3" style={{ border: '1px dashed var(--orbis-s3)', color: 'var(--orbis-t3)' }}>
          No CTA yet.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((cta, index) => (
            <div
              key={`${cta.style}-${index}`}
              className="rounded-xl p-4"
              style={{ background: 'var(--surface-1)', border: '1px solid var(--orbis-s2)' }}
            >
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="inline-flex rounded-full p-1" style={{ background: 'var(--orbis-s2)' }}>
                  {['primary', 'secondary', 'text_link'].map((styleKey) => (
                    <button
                      key={styleKey}
                      onClick={() => updateCta(index, 'style', styleKey)}
                      className="rounded-full px-3 py-1 text-[11px]"
                      style={{
                        background: cta.style === styleKey ? 'var(--orbis-blue)' : 'transparent',
                        color: cta.style === styleKey ? '#09090B' : 'var(--orbis-t2)',
                      }}
                    >
                      {styleKey.replace('_', ' ')}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => removeCta(index)}
                  className="text-[11px]"
                  style={{ color: 'var(--orbis-t3)' }}
                >
                  Remove
                </button>
              </div>
              <input
                value={cta.label || ''}
                placeholder="CTA label"
                onChange={(e) => updateCta(index, 'label', e.target.value)}
                className="w-full rounded-lg px-3 py-2 outline-none"
                style={{
                  background: 'var(--surface-2)',
                  border: '1px solid var(--orbis-s3)',
                  color: 'var(--orbis-t1)',
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContentItemsList({ items, onChange }) {
  const list = items?.length ? items : [];

  const updateItem = (index, nextItem) => {
    const next = [...list];
    next[index] = nextItem;
    onChange(next);
  };

  const addItem = () => {
    onChange([...list, { type: 'feature', title: '', description: '' }]);
  };

  const removeItem = (index) => {
    onChange(list.filter((_, idx) => idx !== index));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <ToneLabel>Supporting Content</ToneLabel>
        <button
          onClick={addItem}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px]"
          style={{ border: '1px solid var(--orbis-s3)', color: 'var(--orbis-t2)' }}
        >
          <Plus className="w-3 h-3" />
          Add Item
        </button>
      </div>
      {list.length === 0 ? (
        <div className="rounded-xl px-4 py-3" style={{ border: '1px dashed var(--orbis-s3)', color: 'var(--orbis-t3)' }}>
          No supporting items yet.
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((item, index) => {
            const Icon = ICON_MAP[item.type] || Layers;
            return (
              <div
                key={`${item.type}-${index}`}
                className="rounded-xl p-4"
                style={{ background: 'var(--surface-1)', border: '1px solid var(--orbis-s2)' }}
              >
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" style={{ color: 'var(--orbis-t3)' }} />
                    <select
                      value={item.type || 'feature'}
                      onChange={(e) => updateItem(index, { ...item, type: e.target.value })}
                      className="rounded-lg px-2 py-1 outline-none"
                      style={{ background: 'var(--surface-2)', color: 'var(--orbis-t1)' }}
                    >
                      <option value="feature">feature</option>
                      <option value="stat">stat</option>
                      <option value="testimonial">testimonial</option>
                      <option value="step">step</option>
                      <option value="pricing_tier">pricing_tier</option>
                    </select>
                  </div>
                  <button
                    onClick={() => removeItem(index)}
                    className="text-[11px]"
                    style={{ color: 'var(--orbis-t3)' }}
                  >
                    Remove
                  </button>
                </div>

                {item.type === 'stat' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      value={item.value || ''}
                      placeholder="99.9%"
                      onChange={(e) => updateItem(index, { ...item, value: e.target.value })}
                      className="rounded-lg px-3 py-2 outline-none"
                      style={{ background: 'var(--surface-2)', border: '1px solid var(--orbis-s3)', color: 'var(--orbis-t1)' }}
                    />
                    <input
                      value={item.label || ''}
                      placeholder="Metric label"
                      onChange={(e) => updateItem(index, { ...item, label: e.target.value })}
                      className="rounded-lg px-3 py-2 outline-none"
                      style={{ background: 'var(--surface-2)', border: '1px solid var(--orbis-s3)', color: 'var(--orbis-t1)' }}
                    />
                  </div>
                ) : item.type === 'testimonial' ? (
                  <div className="space-y-3">
                    <textarea
                      value={item.quote || ''}
                      placeholder="Quote"
                      rows={3}
                      onChange={(e) => updateItem(index, { ...item, quote: e.target.value })}
                      className="w-full rounded-lg px-3 py-2 outline-none resize-none"
                      style={{ background: 'var(--surface-2)', border: '1px solid var(--orbis-s3)', color: 'var(--orbis-t1)' }}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        value={item.author || ''}
                        placeholder="Author"
                        onChange={(e) => updateItem(index, { ...item, author: e.target.value })}
                        className="rounded-lg px-3 py-2 outline-none"
                        style={{ background: 'var(--surface-2)', border: '1px solid var(--orbis-s3)', color: 'var(--orbis-t1)' }}
                      />
                      <input
                        value={item.role || ''}
                        placeholder="Role"
                        onChange={(e) => updateItem(index, { ...item, role: e.target.value })}
                        className="rounded-lg px-3 py-2 outline-none"
                        style={{ background: 'var(--surface-2)', border: '1px solid var(--orbis-s3)', color: 'var(--orbis-t1)' }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <input
                      value={item.title || ''}
                      placeholder="Item title"
                      onChange={(e) => updateItem(index, { ...item, title: e.target.value })}
                      className="w-full rounded-lg px-3 py-2 outline-none"
                      style={{ background: 'var(--surface-2)', border: '1px solid var(--orbis-s3)', color: 'var(--orbis-t1)' }}
                    />
                    <textarea
                      value={item.description || ''}
                      placeholder="Description"
                      rows={3}
                      onChange={(e) => updateItem(index, { ...item, description: e.target.value })}
                      className="w-full rounded-lg px-3 py-2 outline-none resize-none"
                      style={{ background: 'var(--surface-2)', border: '1px solid var(--orbis-s3)', color: 'var(--orbis-t1)' }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PreviewButton({ cta }) {
  const isPrimary = cta.style === 'primary';
  const isTextLink = cta.style === 'text_link';
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full px-4 py-2"
      style={{
        background: isPrimary ? 'var(--orbis-blue)' : isTextLink ? 'transparent' : 'var(--surface-2)',
        border: isTextLink ? 'none' : `1px solid ${isPrimary ? 'transparent' : 'var(--orbis-s3)'}`,
        color: isPrimary ? '#09090B' : 'var(--orbis-t1)',
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      <MousePointerClick className="w-3 h-3" />
      {safeText(cta.label) || 'Untitled CTA'}
    </span>
  );
}

function ContentItemsPreview({ items }) {
  if (!items?.length) {
    return <p style={{ color: 'var(--orbis-t4)', fontSize: 13 }}>No supporting content yet.</p>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {items.map((item, index) => {
        const Icon = ICON_MAP[item.type] || Layers;
        return (
          <div
            key={`${item.type}-${index}`}
            className="rounded-xl p-4"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--orbis-s2)' }}
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'var(--surface-2)' }}>
                <Icon className="w-4 h-4" style={{ color: 'var(--orbis-t2)' }} />
              </div>
              <div className="space-y-1 min-w-0">
                <ToneLabel>{item.type}</ToneLabel>
                {item.type === 'stat' ? (
                  <>
                    <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--orbis-t1)' }}>{safeText(item.value) || '0'}</div>
                    <p style={{ color: 'var(--orbis-t2)', fontSize: 13 }}>{safeText(item.label) || 'Metric label'}</p>
                  </>
                ) : item.type === 'testimonial' ? (
                  <>
                    <p style={{ color: 'var(--orbis-t1)', fontSize: 14, lineHeight: 1.6, fontStyle: 'italic' }}>
                      {safeText(item.quote) || 'Customer quote'}
                    </p>
                    <p style={{ color: 'var(--orbis-t3)', fontSize: 12 }}>
                      {[safeText(item.author), safeText(item.role)].filter(Boolean).join(' — ') || 'Attribution'}
                    </p>
                  </>
                ) : (
                  <>
                    <div style={{ color: 'var(--orbis-t1)', fontSize: 15, fontWeight: 500 }}>{safeText(item.title) || 'Untitled item'}</div>
                    <p style={{ color: 'var(--orbis-t3)', fontSize: 13, lineHeight: 1.6 }}>
                      {safeText(item.description) || 'Add supporting detail for this section.'}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ArtDirectionPanel({ section, activeField, onEditField, onUpdateField }) {
  const assetInfo = ASSET_TYPE_LABELS[section.asset_type] || ASSET_TYPE_LABELS.photo;

  return (
    <div
      className="rounded-2xl p-5"
      style={{
        background: 'linear-gradient(180deg, rgba(24,24,27,0.92) 0%, rgba(39,39,42,0.56) 100%)',
        border: '1px solid rgba(212,184,150,0.18)',
      }}
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Camera className="w-4 h-4" style={{ color: 'var(--orbis-amber)' }} />
          <ToneLabel tone="accent">Art Direction</ToneLabel>
        </div>
        <div className="inline-flex rounded-full p-1" style={{ background: 'rgba(0,0,0,0.18)' }}>
          {Object.entries(ASSET_TYPE_LABELS).map(([key, meta]) => (
            <button
              key={key}
              onClick={() => onUpdateField('asset_type', key)}
              className="rounded-full px-3 py-1 text-[11px]"
              style={{
                background: section.asset_type === key ? meta.color : 'transparent',
                color: section.asset_type === key ? '#09090B' : 'var(--orbis-t2)',
              }}
            >
              {meta.label}
            </button>
          ))}
        </div>
      </div>
      {activeField === 'art_direction' ? (
        <InlineTextEdit
          label="Image Direction"
          value={section.image_direction}
          onChange={(value) => onUpdateField('image_direction', value)}
          multiline
          placeholder="Describe composition, mood, lighting, and subject direction."
        />
      ) : (
        <button onClick={() => onEditField('art_direction')} className="w-full text-left">
          <p style={{ fontSize: 15, lineHeight: 1.8, color: 'var(--orbis-t1)' }}>
            {section.image_direction || 'Add art direction for imagery, composition, and mood.'}
          </p>
          <div className="mt-4 flex items-center justify-between gap-3">
            <span
              className="rounded-full px-3 py-1.5 text-[11px] font-medium"
              style={{
                background: `${assetInfo.color}1F`,
                color: assetInfo.color,
                border: `1px solid ${assetInfo.color}33`,
              }}
            >
              {assetInfo.label}
            </span>
            <span style={{ color: 'var(--orbis-t4)', fontSize: 12 }}>Click to refine</span>
          </div>
        </button>
      )}
      {/* Section Design Direction — editable creative brief for the Build AI */}
      {section.section_design_direction != null && (
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--orbis-s2)' }}>
          <div className="flex items-center gap-2 mb-2">
            <span style={{ fontFamily: 'var(--orbis-mono)', fontSize: 10, letterSpacing: '0.1em', color: 'var(--orbis-t3)', textTransform: 'uppercase' }}>
              Section Design Spec
            </span>
          </div>
          {activeField === 'design_direction' ? (
            <InlineTextEdit
              label="Design Direction"
              value={section.section_design_direction}
              onChange={(value) => onUpdateField('section_design_direction', value)}
              multiline
              placeholder="Layout treatment, typography, animation, and interaction spec for the Build AI."
            />
          ) : (
            <button onClick={() => onEditField('design_direction')} className="w-full text-left">
              <p style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--orbis-t2)' }}>
                {section.section_design_direction || 'No design direction yet — the Build AI will use default patterns.'}
              </p>
              {section.section_design_direction && (
                <span style={{ color: 'var(--orbis-t4)', fontSize: 11, marginTop: 6, display: 'inline-block' }}>Click to refine</span>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SectionPreview({ section, activeField, onEditField }) {
  return (
    <div className="space-y-6">
      <button onClick={() => onEditField('headline')} className="block w-full text-left">
        <ToneLabel>{SECTION_LABELS.headline}</ToneLabel>
        <h3 style={{ fontSize: 30, lineHeight: 1.12, letterSpacing: '-0.03em', color: 'var(--orbis-t1)', marginTop: 10 }}>
          {safeText(section.headline) || 'Add a section headline'}
        </h3>
      </button>

      {(section.subheadline || activeField === 'subheadline') && (
        <button onClick={() => onEditField('subheadline')} className="block w-full text-left">
          <ToneLabel>{SECTION_LABELS.subheadline}</ToneLabel>
          <p style={{ color: 'var(--orbis-t2)', fontSize: 16, lineHeight: 1.7, marginTop: 8 }}>
            {safeText(section.subheadline) || 'Add supporting copy'}
          </p>
        </button>
      )}

      <button onClick={() => onEditField('body_copy')} className="block w-full text-left">
        <ToneLabel>{SECTION_LABELS.body_copy}</ToneLabel>
        <p style={{ color: 'var(--orbis-t2)', fontSize: 14, lineHeight: 1.85, marginTop: 10, maxWidth: 720 }}>
          {safeText(section.body_copy) || 'Add body copy for this section.'}
        </p>
      </button>

      <button onClick={() => onEditField('ctas')} className="block w-full text-left">
        <div className="flex items-center justify-between mb-3">
          <ToneLabel>{SECTION_LABELS.ctas}</ToneLabel>
          {!section.ctas?.length && <span style={{ color: 'var(--orbis-t4)', fontSize: 12 }}>Click to add</span>}
        </div>
        <div className="flex flex-wrap gap-2">
          {section.ctas?.length ? section.ctas.map((cta, index) => <PreviewButton key={`${cta.style}-${index}`} cta={cta} />) : (
            <span className="rounded-full px-4 py-2" style={{ border: '1px dashed var(--orbis-s3)', color: 'var(--orbis-t3)' }}>
              Add CTA
            </span>
          )}
        </div>
      </button>

      <button onClick={() => onEditField('content_items')} className="block w-full text-left">
        <div className="flex items-center justify-between mb-3">
          <ToneLabel>{SECTION_LABELS.content_items}</ToneLabel>
          {!section.content_items?.length && <span style={{ color: 'var(--orbis-t4)', fontSize: 12 }}>Click to add</span>}
        </div>
        <ContentItemsPreview items={section.content_items} />
      </button>
    </div>
  );
}

function SectionEditFields({ section, activeField, onUpdateField, onUpdateCtas, onUpdateItems }) {
  if (!activeField) return null;

  if (activeField === 'ctas') {
    return <CTAEditor ctas={section.ctas} onChange={onUpdateCtas} />;
  }

  if (activeField === 'content_items') {
    return <ContentItemsList items={section.content_items} onChange={onUpdateItems} />;
  }

  if (activeField === 'headline' || activeField === 'subheadline' || activeField === 'body_copy') {
    return (
      <InlineTextEdit
        label={SECTION_LABELS[activeField]}
        value={section[activeField]}
        onChange={(value) => onUpdateField(activeField, value)}
        multiline={activeField === 'body_copy'}
        placeholder={activeField === 'headline' ? 'Section headline' : activeField === 'subheadline' ? 'Supporting line' : 'Body copy'}
      />
    );
  }

  return null;
}

function ContentSectionCard({
  section,
  index,
  isDirty,
  reducedMotion,
  onChange,
}) {
  const [expanded, setExpanded] = useState(true);
  const [activeField, setActiveField] = useState(null);
  const assetInfo = ASSET_TYPE_LABELS[section.asset_type] || ASSET_TYPE_LABELS.photo;

  const updateField = (field, value) => {
    onChange({ ...section, [field]: value });
  };

  return (
    <motion.div
      initial={reducedMotion ? {} : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04, duration: 0.24 }}
      className="rounded-[24px] overflow-hidden"
      style={{ background: 'var(--surface-1)', border: '1px solid var(--orbis-s2)' }}
    >
      <button
        onClick={() => setExpanded((value) => !value)}
        className="w-full px-5 py-4 flex items-center justify-between text-left"
        style={{ borderBottom: expanded ? '1px solid var(--orbis-s2)' : 'none' }}
      >
        <div className="flex items-center gap-4">
          <div
            className="w-11 h-11 rounded-2xl flex flex-col items-center justify-center"
            style={{ background: 'var(--surface-2)', border: '1px solid var(--orbis-s3)' }}
          >
            <span style={{ fontSize: 10, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)' }}>Sec</span>
            <span style={{ fontSize: 15, color: 'var(--orbis-t1)', fontWeight: 600 }}>{index + 1}</span>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span style={{ color: 'var(--orbis-t1)', fontSize: 15, fontWeight: 500 }}>
                {section.section_name || section.section_type}
              </span>
              {isDirty && (
                <span
                  className="rounded-full px-2 py-0.5"
                  style={{ background: 'rgba(184,204,223,0.12)', color: 'var(--orbis-blue)', fontSize: 10, fontFamily: 'var(--orbis-mono)' }}
                >
                  edited
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <ToneLabel>{section.section_type}</ToneLabel>
              <span
                className="rounded-full px-2 py-0.5"
                style={{
                  background: `${assetInfo.color}20`,
                  color: assetInfo.color,
                  fontSize: 10,
                  fontWeight: 600,
                }}
              >
                {assetInfo.label}
              </span>
            </div>
          </div>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4" style={{ color: 'var(--orbis-t3)' }} /> : <ChevronRight className="w-4 h-4" style={{ color: 'var(--orbis-t3)' }} />}
      </button>

      {expanded && (
        <div className="px-5 py-5">
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.5fr)_360px] gap-6">
            <div className="space-y-5">
              <SectionPreview section={section} activeField={activeField} onEditField={setActiveField} />
              {activeField && activeField !== 'art_direction' && (
                <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--orbis-s2)' }}>
                  <div className="flex items-center justify-between mb-4">
                    <ToneLabel tone="accent">Editing {SECTION_LABELS[activeField]}</ToneLabel>
                    <button onClick={() => setActiveField(null)} className="text-[11px]" style={{ color: 'var(--orbis-t3)' }}>
                      Done
                    </button>
                  </div>
                  <SectionEditFields
                    section={section}
                    activeField={activeField}
                    onUpdateField={updateField}
                    onUpdateCtas={(ctas) => onChange({ ...section, ctas })}
                    onUpdateItems={(content_items) => onChange({ ...section, content_items })}
                  />
                </div>
              )}
            </div>
            <ArtDirectionPanel
              section={section}
              activeField={activeField}
              onEditField={setActiveField}
              onUpdateField={updateField}
            />
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function ContentStage({
  content,
  structure,
  onGenerate,
  onApprove,
  isProcessing,
  projectId,
  fallbackReason,
  onAcceptFallback,
}) {
  const reducedMotion = useReducedMotion();
  const [localContent, setLocalContent] = useState(content || null);
  const [dirtySectionIds, setDirtySectionIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setLocalContent(content || null);
    setDirtySectionIds([]);
  }, [content]);

  const sections = useMemo(() => localContent?.sections || content?.sections || [], [localContent, content]);

  const pageGroups = useMemo(() => {
    if (!structure?.pages) return [{ name: 'Home', sections }];

    const groups = [];
    const sectionMap = {};
    sections.forEach((section) => {
      sectionMap[section.section_id] = section;
    });

    for (const page of structure.pages) {
      const pageSections = [];
      for (const sec of (page.sections || [])) {
        if (sectionMap[sec.id]) pageSections.push(sectionMap[sec.id]);
      }
      if (pageSections.length) {
        groups.push({ name: page.name || 'Page', sections: pageSections });
      }
    }

    const matched = new Set(groups.flatMap((group) => group.sections.map((section) => section.section_id)));
    const orphans = sections.filter((section) => !matched.has(section.section_id));
    if (orphans.length) groups.push({ name: 'Other', sections: orphans });

    return groups.length ? groups : [{ name: 'Sections', sections }];
  }, [sections, structure]);

  const hasUnsavedChanges = dirtySectionIds.length > 0;

  const handleSectionChange = (sectionId, updatedSection) => {
    const nextSections = sections.map((section) => (
      section.section_id === sectionId ? updatedSection : section
    ));
    setLocalContent({
      ...(localContent || content || {}),
      sections: nextSections,
    });
    setDirtySectionIds((prev) => (prev.includes(sectionId) ? prev : [...prev, sectionId]));
  };

  const saveContent = async () => {
    if (!projectId || !hasUnsavedChanges) return true;

    setIsSaving(true);
    try {
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/edit-content`, {
        content: localContent,
      });
      setLocalContent(response.data.content || localContent);
      setDirtySectionIds([]);
      toast.success('Content edits saved');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save content edits');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleApprove = async () => {
    if (isSaving) return;
    if (hasUnsavedChanges) {
      const didSave = await saveContent();
      if (!didSave) return;
    }
    await onApprove();
  };

  if (!content && !isProcessing) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-4">
          <div
            className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center"
            style={{ background: 'var(--orbis-s1)', border: '1px solid var(--orbis-border-md)' }}
          >
            <PenLine className="w-7 h-7" style={{ color: 'var(--orbis-blue)' }} />
          </div>
          <div>
            <h3 className="text-lg font-semibold" style={{ color: 'var(--orbis-t1)' }}>
              Content & Art Direction
            </h3>
            <p className="text-sm mt-1 max-w-md mx-auto" style={{ color: 'var(--orbis-t3)' }}>
              An AI creative director will write all headlines, copy, CTAs, and image art direction
              for your website.
            </p>
          </div>
          <button
            onClick={onGenerate}
            disabled={isProcessing}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium"
            style={{ background: 'var(--orbis-blue)', color: '#09090B' }}
          >
            <Sparkles className="w-4 h-4" />
            Generate Content
          </button>
        </div>
      </div>
    );
  }

  if (isProcessing && !content) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-4">
          <OrbisSpinner size={48} />
          <p className="text-sm" style={{ color: 'var(--orbis-t2)' }}>
            Writing copy & art direction...
          </p>
        </div>
      </div>
    );
  }

  let globalIndex = 0;

  return (
    <div className="flex flex-col w-full" style={{ height: 'calc(100vh - 64px)', background: 'var(--surface-0)' }}>
      <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid var(--orbis-border-md)' }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'var(--orbis-s2)' }}>
            <PenLine className="w-4 h-4" style={{ color: 'var(--orbis-blue)' }} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-base font-semibold" style={{ color: 'var(--orbis-t1)' }}>
                Content & Art Direction
              </h2>
              {hasUnsavedChanges && (
                <span
                  className="rounded-full px-2 py-0.5"
                  style={{ background: 'rgba(184,204,223,0.12)', color: 'var(--orbis-blue)', fontSize: 10, fontFamily: 'var(--orbis-mono)' }}
                >
                  {dirtySectionIds.length} unsaved
                </span>
              )}
            </div>
            <p className="text-xs" style={{ color: 'var(--orbis-t3)' }}>
              Review the copy hierarchy, supporting content, and art direction before generating assets.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onGenerate}
            disabled={isProcessing || isSaving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium"
            style={{
              background: 'var(--orbis-s2)',
              color: 'var(--orbis-t2)',
              border: '1px solid var(--orbis-border-md)',
            }}
          >
            <RefreshCw className={`w-3 h-3 ${isProcessing ? 'animate-spin' : ''}`} />
            Regenerate
          </button>
          <button
            onClick={handleApprove}
            disabled={isProcessing || isSaving || !!fallbackReason}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-medium"
            style={{ background: 'var(--orbis-blue)', color: '#09090B', opacity: fallbackReason ? 0.4 : 1 }}
          >
            {(isSaving || isProcessing) ? <OrbisSpinner size={14} color="#09090B" /> : <Check className="w-3.5 h-3.5" />}
            {isSaving ? 'Saving...' : 'Approve & Generate Assets'}
          </button>
        </div>
      </div>

      {/* ── FALLBACK BANNER ── */}
      {fallbackReason && (
        <div className="mx-6 mb-3 flex items-center gap-3" style={{
          background: 'rgba(180, 130, 50, 0.12)', border: '1px solid rgba(212, 184, 150, 0.25)',
          borderRadius: 'var(--orbis-r-lg)', padding: '10px 14px', fontFamily: 'var(--orbis-font)',
        }}>
          <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--orbis-amber)' }} />
          <div style={{ flex: 1, fontSize: '12px', color: 'var(--orbis-t2)', lineHeight: '1.5' }}>
            <span style={{ color: 'var(--orbis-amber)', fontWeight: 600 }}>Draft mode</span> — AI content generation failed ({fallbackReason}). This is placeholder copy.
          </div>
          <button
            onClick={onGenerate}
            disabled={isProcessing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium"
            style={{ background: 'var(--orbis-amber)', color: '#09090B', whiteSpace: 'nowrap' }}
          >
            <RefreshCw className={`w-3 h-3 ${isProcessing ? 'animate-spin' : ''}`} />
            Retry with AI
          </button>
          <button
            onClick={onAcceptFallback}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs"
            style={{ background: 'var(--orbis-s2)', color: 'var(--orbis-t3)', whiteSpace: 'nowrap' }}
          >
            Use draft
          </button>
        </div>
      )}

      {(localContent?.brand_voice_summary || content?.brand_voice_summary) && (
        <div className="px-6 pt-4 pb-2 sticky top-0 z-10" style={{ background: 'linear-gradient(180deg, rgba(9,9,11,1) 0%, rgba(9,9,11,0.96) 70%, rgba(9,9,11,0) 100%)' }}>
          <div
            className="rounded-2xl px-5 py-4"
            style={{ background: 'var(--surface-1)', border: '1px solid var(--orbis-border-md)' }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Type className="w-3.5 h-3.5" style={{ color: 'var(--orbis-amber)' }} />
              <ToneLabel tone="accent">Brand Voice</ToneLabel>
            </div>
            <p style={{ color: 'var(--orbis-t2)', fontSize: 13, lineHeight: 1.7 }}>
              {localContent?.brand_voice_summary || content?.brand_voice_summary}
            </p>
          </div>
        </div>
      )}

      <ScrollArea className="flex-1 px-6 pb-6">
        <div className="space-y-8 pb-10">
          {pageGroups.map((group) => (
            <section key={group.name} className="space-y-4">
              {pageGroups.length > 1 && (
                <div className="flex items-center gap-3 pt-2">
                  <div className="h-px flex-1" style={{ background: 'var(--orbis-s2)' }} />
                  <ToneLabel>{group.name}</ToneLabel>
                  <div className="h-px flex-1" style={{ background: 'var(--orbis-s2)' }} />
                </div>
              )}
              <div className="space-y-4">
                {group.sections.map((section) => {
                  const sectionIndex = globalIndex;
                  globalIndex += 1;
                  return (
                    <ContentSectionCard
                      key={section.section_id}
                      section={section}
                      index={sectionIndex}
                      isDirty={dirtySectionIds.includes(section.section_id)}
                      reducedMotion={reducedMotion}
                      onChange={(updatedSection) => handleSectionChange(section.section_id, updatedSection)}
                    />
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
